<template>
  <div class="navbar-wrapper fixed-top">
    <!-- Promo banner - Optimizado para móviles -->
    <div class="promo-banner">
      <div
        class="container px-3 py-2 d-flex justify-content-between gap-3 text-lg-start text-center align-items-center flex-wrap py-1">
        <div
          class="promo-text d-flex align-items-center justify-content-center flex-wrap gap-3 gap-md-4 text-white fw-light">
          <div class="promo-item">
            <FaIcon name="phone" class="me-2" />
            <a href="tel:+5491158162028" class="text-white text-decoration-none fw-normal fs-7">11 5816-2028</a>
          </div>
          <div class="promo-item d-none d-sm-block">
            <FaIcon name="envelope" class="me-2" />
            <a href="mailto:jamrockgrowshop@hotmail.com"
              class="text-white text-decoration-none fw-normal fs-7">jamrockgrowshop@hotmail.com</a>
          </div>
        </div>
        <div class="promo-links d-flex gap-3 gap-md-4">
          <a href="https://instagram.com/jamrockgrowshop" target="_blank"
            class="text-white text-decoration-none fw-normal fs-7">
            <FaIcon name="instagram" brand />
            <span class="d-none d-md-inline ms-2">Instagram</span>
          </a>
          <a href="https://wa.me/+5491158162028" target="_blank" class="text-white text-decoration-none fw-normal fs-7">
            <FaIcon name="whatsapp" brand />
            <span class="d-none d-md-inline ms-2">WhatsApp</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Navbar principal -->
    <nav class="navbar navbar-expand-lg pb-0">
      <div class="container">
        <div class="d-flex align-items-center justify-content-between w-100">
          <!-- Logo -->
          <a class="navbar-brand" href="/">
            <div class="brand-container">
              <img src="@/assets/images/jamrock-logo.webp"
                 alt="Jamrock Growshop Logo"
                 class="logo-img"
                 width="263"
                 height="313"
                 fetchpriority="high"
                 decoding="async"
                 loading="eager" />
            </div>
          </a>

          <!-- Desktop Search Bar -->
          <div class="search-container d-none d-lg-block ms-4">
            <form @submit.prevent="handleSearch" class="input-group position-relative">
              <input type="text" class="form-control search-input bg-white border border-1 fs-7"
                placeholder="Buscar productos..." v-model="searchQuery" @input="handleSearchInput" @blur="handleBlur"
                @focus="showSuggestions = true" aria-label="Search products" />
              <button class="btn search-btn border border-1 border-white" type="submit" aria-label="Search">
                <FaIcon name="search" />
              </button>

              <!-- Search Suggestions Dropdown -->
              <div class="search-suggestions" v-if="showSuggestions">
                <!-- Loading State -->
                <div v-if="isSearching" class="search-loading">
                  <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span class="visually-hidden">Buscando...</span>
                  </div>
                  <span class="text-muted">Buscando productos...</span>
                </div>
                <!-- Results -->
                <template v-else-if="suggestions.length > 0">
                  <div v-for="product in suggestions" :key="product.id" class="suggestion-item"
                    @mousedown.prevent="handleSuggestionClick(product)">
                    <img :src="suggestionImgSrc(product.image)" class="suggestion-img"
                      :alt="product.name" loading="lazy">
                    <div class="suggestion-info">
                      <div class="suggestion-name">{{ product.name }}</div>
                      <div class="suggestion-price">${{ product.price }}</div>
                    </div>
                  </div>
                </template>
                <!-- No Results -->
                <div v-else-if="searchQuery.length >= 2" class="search-no-results">
                  <FaIcon name="search" class="text-muted me-2" />
                  <span class="text-muted">No se encontraron productos</span>
                </div>
              </div>
            </form>
          </div>

          <!-- Desktop Actions -->
          <div class="nav-actions d-none d-lg-flex align-items-center">
            <a @click.prevent="handleUserAccount" href="#"
              class="user-account text-decoration-none text-white d-flex align-items-center gap-2">
              <FaIcon name="user" />
              <div class="account-text">
                <span class="account-label">{{ username }}</span>
                <span class="badge badge-pill account-role bg-primary" v-if="isAdmin">
                  Admin
                </span>
              </div>
            </a>
            <a href="/carrito" class="cart-icon ms-0 text-decoration-none">
              <FaIcon name="shopping-cart" class="text-white" />
              <span class="badge badge-pill cart-count" v-if="cartCount > 0">{{
                cartCount
              }}</span>
            </a>
            <!-- Botón para abrir menú de categorías en desktop -->
            <button class="desktop-menu-toggle ms-2 border-0 bg-transparent" @click="toggleDesktopMenu"
              aria-label="Toggle categories menu" type="button">
              <FaIcon name="bars" class="text-white" />
            </button>
          </div> <!-- Mobile Actions - Enhanced for better UX -->
          <div class="mobile-actions d-lg-none ms-auto d-flex align-items-center">
            <!-- Mobile Search Button -->
            <button @click="toggleMobileSearch"
              class="mobile-icon-btn me-2 d-flex align-items-center justify-content-center"
              aria-label="Search products">
              <FaIcon name="search" class="text-white" />
            </button>

            <!-- Cart Icon -->
            <a href="/carrito"
              class="mobile-icon-btn me-2 position-relative d-flex align-items-center justify-content-center">
              <FaIcon name="shopping-cart" class="text-white" />
              <span class="badge badge-pill cart-count" v-if="cartCount > 0">{{
                cartCount
              }}</span>
            </a>

            <!-- Menu Toggle -->
            <button class="mobile-menu-toggle mobile-icon-btn border-0" @click="toggleMobileMenu"
              :aria-expanded="isMobileMenuOpen" aria-label="Toggle navigation menu" type="button">
              <FaIcon :name="isMobileMenuOpen ? 'times' : 'bars'" class="text-white" />
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- Desktop Category Menu (vacío: el menú de categorías vive en el offcanvas) -->

    <!-- Desktop Offcanvas Menu (nuevo) -->
    <div class="desktop-offcanvas-menu" :class="{ show: isDesktopMenuOpen }">
      <div class="offcanvas-header">
        <div class="d-flex align-items-center">
          <h5 class="offcanvas-title fs-5">Categorías</h5>
        </div>
        <button type="button" class="btn-close" @click="closeDesktopMenu" aria-label="Close"></button>
      </div>

      <div class="offcanvas-body">
        <!-- Menu Accordion -->
        <div class="desktop-menu accordion" id="desktopMenuAccordion">
          <!-- Skeleton Loaders -->
          <div v-if="isLoadingCategories" class="desktop-skeleton-menu">
            <div v-for="index in 5" :key="index" class="desktop-skeleton-item">
              <div class="skeleton-button"></div>
            </div>
          </div>

          <!-- Categorías dinámicas -->
          <template v-else>
            <!-- Links fijos -->
             <div class="desktop-menu-item">
              <a href="/blog" class="desktop-link" @click="closeDesktopMenu">
                <FaIcon name="blog" class="me-3" />
                BLOG
              </a>
            </div>
            <div class="desktop-menu-item">
              <a href="/productos" class="desktop-link" @click="closeDesktopMenu">
                <FaIcon name="cube" class="me-3" />
                TODOS LOS PRODUCTOS
              </a>
            </div>
            <div class="desktop-menu-item">
              <a href="https://semillerodeloeste.com.ar" target="_blank" rel="noopener noreferrer" class="desktop-link"
                @click="closeDesktopMenu">
                <FaIcon name="seedling" class="me-3" />
                SEMILLAS
              </a>
            </div>
            

            <!-- Categorías padre -->
            <template v-for="category in parentCategories" :key="category.id">
              <!-- Categoría con subcategorías -->
              <div v-if="hasSubcategories(category.id)" class="accordion-item border-0">
                <h2 class="accordion-header" :id="'desktopHeading' + category.id">
                  <button class="accordion-button collapsed desktop-link" type="button" data-bs-toggle="collapse"
                    :data-bs-target="'#desktopCollapse' + category.id">
                    <FaIcon :name="getCategoryIcon(category.name)" class="me-3" />
                    {{ category.name.toUpperCase() }}
                  </button>
                </h2>
                <div :id="'desktopCollapse' + category.id" class="accordion-collapse collapse"
                  :aria-labelledby="'desktopHeading' + category.id" data-bs-parent="#desktopMenuAccordion">
                  <div class="accordion-body p-0">
                    <div class="submenu-items">
                      <!-- Subcategorías (hijas) -->
                      <template v-for="subcat in getDirectSubcategories(category.id)" :key="subcat.id">
                        <!-- Subcategoría con nietas -->
                        <div v-if="hasSubcategories(subcat.id)" class="subcategory-with-children mb-3">
                          <a :href="categoryHref(subcat)" class="submenu-link subcategory-parent-link"
                            @click="closeDesktopMenu">
                            {{ subcat.name }}
                          </a>
                          <div class="subcategory-children ps-3 mt-2">
                            <a v-for="grandchild in getDirectSubcategories(subcat.id)" :key="grandchild.id"
                              :href="categoryHref(grandchild)" class="submenu-link subgrandchild-link"
                              @click="closeDesktopMenu">
                              {{ grandchild.name }}
                            </a>
                          </div>
                        </div>
                        <!-- Subcategoría sin nietas -->
                        <a v-else :href="categoryHref(subcat)" class="submenu-link"
                          @click="closeDesktopMenu">
                          {{ subcat.name }}
                        </a>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Categoría sin subcategorías -->
              <div v-else class="desktop-menu-item">
                <a :href="categoryHref(category)" class="desktop-link" @click="closeDesktopMenu">
                  <FaIcon :name="getCategoryIcon(category.name)" class="me-3" />
                  {{ category.name.toUpperCase() }}
                </a>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>

    <!-- Overlay for desktop menu -->
    <div class="desktop-offcanvas-backdrop" :class="{ show: isDesktopMenuOpen }" @click="closeDesktopMenu"></div>

    <!-- Mobile Offcanvas Menu -->
    <div class="offcanvas-menu" :class="{ show: isMobileMenuOpen }">
      <div class="offcanvas-header">
        <div class="d-flex align-items-center">
          <h5 class="offcanvas-title">Categorías</h5>
        </div>
        <button
          type="button"
          class="btn-close"
          @click="closeMobileMenu"
          aria-label="Close"
        ></button>
      </div>

      <div class="offcanvas-body">
        <!-- Mobile Search Bar -->
        <div class="search-container mb-4">
          <form @submit.prevent="handleSearch" class="input-group">
            <input
              type="text"
              class="form-control search-input fs-7"
              placeholder="Buscar productos..."
              v-model="searchQuery"
              aria-label="Search products"
            />
            <button class="btn search-btn" type="submit" aria-label="Search">
              <FaIcon name="search" />
            </button>
          </form>
        </div>

        <!-- User Account Link -->
        <div class="mobile-user-account mb-4">
          <a
            @click.prevent="handleUserAccount"
            href="#"
            class="d-flex align-items-center p-2 rounded-3"
          >
            <FaIcon name="user" class="me-3 fs-5" />
            <div>
              <span class="d-block fw-medium">{{ username }}</span>
              <small class="text-muted">Mi cuenta</small>
            </div>
            <FaIcon name="chevron-right" class="ms-auto" />
          </a>
        </div>

        <!-- Menu Accordion - Igual que en desktop -->
        <div class="mobile-menu accordion" id="mobileMenuAccordion">
          <!-- Skeleton Loaders -->
          <div v-if="isLoadingCategories" class="mobile-skeleton-menu">
            <div v-for="index in 5" :key="index" class="mobile-skeleton-item">
              <div class="skeleton-button"></div>
            </div>
          </div>
          
          <!-- Categorías dinámicas -->
          <template v-else>
            <!-- Links fijos -->
              <div class="mobile-menu-item">
              <a href="/blog" class="mobile-link" @click="closeMobileMenu">
                <FaIcon name="blog" class="me-3" />
                BLOG
              </a>
            </div>
            <div class="mobile-menu-item">
              <a href="/productos" class="mobile-link" @click="closeMobileMenu">
                <FaIcon name="cube" class="me-3" />
                TODOS LOS PRODUCTOS
              </a>
            </div>
            <div class="mobile-menu-item">
              <a href="https://semillerodeloeste.com.ar" target="_blank" rel="noopener noreferrer" class="mobile-link" @click="closeMobileMenu">
                <FaIcon name="seedling" class="me-3" />
                SEMILLAS
              </a>
            </div>
           
            
            <!-- Categorías padre -->
            <template v-for="category in parentCategories" :key="category.id">
              <!-- Categoría con subcategorías -->
              <div v-if="hasSubcategories(category.id)" class="accordion-item border-0">
                <h2 class="accordion-header" :id="'heading' + category.id">
                  <button class="accordion-button collapsed mobile-link" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          :data-bs-target="'#collapse' + category.id">
                    <FaIcon :name="getCategoryIcon(category.name)" class="me-3" />
                    {{ category.name.toUpperCase() }}
                  </button>
                </h2>
                <div :id="'collapse' + category.id" 
                     class="accordion-collapse collapse" 
                     :aria-labelledby="'heading' + category.id" 
                     data-bs-parent="#mobileMenuAccordion">
                  <div class="accordion-body p-0">
                    <div class="submenu-items mobile-submenu-items">
                      <!-- Subcategorías (hijas) -->
                      <template v-for="subcat in getDirectSubcategories(category.id)" :key="subcat.id">
                        <!-- Subcategoría con nietas -->
                        <div v-if="hasSubcategories(subcat.id)" class="subcategory-with-children mb-3">
                          <a :href="categoryHref(subcat)" 
                             class="mobile-subcategory-link subcategory-parent-link"
                             @click="closeMobileMenu">
                            {{ subcat.name }}
                          </a>
                          <div class="mobile-subcategory-children ps-3 mt-2">
                            <a v-for="grandchild in getDirectSubcategories(subcat.id)" 
                               :key="grandchild.id"
                               :href="categoryHref(grandchild)" 
                               class="mobile-grandchild-link subgrandchild-link"
                               @click="closeMobileMenu">
                              {{ grandchild.name }}
                            </a>
                          </div>
                        </div>
                        <!-- Subcategoría sin nietas -->
                        <a v-else
                           :href="categoryHref(subcat)" 
                           class="mobile-subcategory-link"
                           @click="closeMobileMenu">
                          {{ subcat.name }}
                        </a>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Categoría sin subcategorías -->
              <div v-else class="mobile-menu-item">
                <a :href="categoryHref(category)" 
                   class="mobile-link" 
                   @click="closeMobileMenu">
                  <FaIcon :name="getCategoryIcon(category.name)" class="me-3" />
                  {{ category.name.toUpperCase() }}
                </a>
              </div>
            </template>
          </template>
        </div>
      </div>

      <!-- Social Links Footer -->
      <div class="offcanvas-footer">
        <div class="d-flex justify-content-between align-items-center">
          <div class="social-links-mobile">
            <a
              href="https://www.facebook.com/profile.php?id=222"
              target="_blank"
              class="social-link-mobile"
            >
              <FaIcon name="facebook" brand />
            </a>
            <a
              href="https://www.instagram.com/jamrockgrowshop"
              target="_blank"
              class="social-link-mobile"
            >
              <FaIcon name="instagram" brand />
            </a>
            <a
              href="https://wa.me/+549112345"
              target="_blank"
              class="social-link-mobile"
            >
              <FaIcon name="whatsapp" brand />
            </a>
          </div>
          <small class="text-muted">© 2023 Jamrock Growshop</small>
        </div>
      </div>
    </div>

    <!-- Overlay for mobile menu -->
    <div class="offcanvas-backdrop" :class="{ show: isMobileMenuOpen }" @click="closeMobileMenu"></div>

    <!-- Mobile Search Overlay -->
    <div class="mobile-search-overlay bg-black" :class="{ show: isMobileSearchOpen }">
      <div class="mobile-search-header">
        <h5 class="text-white m-0">Buscar productos</h5>
        <button class="mobile-search-close" @click="closeMobileSearch" aria-label="Close search">
          <FaIcon name="times" />
        </button>
      </div>
      <div class="mobile-search-form">
        <form @submit.prevent="mobileSearch" class="input-group position-relative">
          <input type="text"
            class="form-control search-input mobile-search-input bg-transparent text-white fs-5"
            placeholder="¿Qué estás buscando?" v-model="searchQuery" @input="handleSearchInput"
            aria-label="Search products" autocomplete="off" />
          <button class="btn search-btn" type="submit" aria-label="Search">
            <FaIcon name="search" />
          </button>
        </form>

        <!-- Mobile Search Suggestions -->
        <div class="search-suggestions mt-4" v-if="showSuggestions">
          <!-- Loading State -->
          <div v-if="isSearching" class="search-loading">
            <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span class="visually-hidden">Buscando...</span>
            </div>
            <span>Buscando productos...</span>
          </div>
          <!-- Results -->
          <template v-else-if="suggestions.length > 0">
            <div v-for="product in suggestions" :key="product.id" class="suggestion-item"
              @click="handleSuggestionClick(product)">
              <img :src="suggestionImgSrc(product.image)" :alt="product.name"
                class="suggestion-img" loading="lazy">
              <div class="suggestion-info">
                <div class="suggestion-name">{{ product.name }}</div>
                <div class="suggestion-price">${{ product.price }}</div>
              </div>
            </div>
          </template>
          <!-- No Results -->
          <div v-else-if="searchQuery.length >= 2" class="search-no-results">
            <FaIcon name="search" class="me-2" />
            <span>No se encontraron productos</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { getCategories } from '@/services/categories';
import { getSearchSuggestions } from '@/services/products';
import { cleanMediaUrl } from '@/utils/text';

export default {
  name: "NavbarComponent", data() {
    return {
      searchQuery: "",
      suggestions: [],
      showSuggestions: false,
      isSearching: false,
      searchTimeout: null,
      activeDropdown: null,
      isMobileMenuOpen: false,
      isDesktopMenuOpen: false,
      isMobileSearchOpen: false,
      categories: [],
      isLoadingCategories: false,
      isFallbackCategories: false,
    };
  },
  created() {
    // Mejorar el logging para depuración
    this.loadCategories();
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const cartCount = computed(() => store.getters.cartCount);
    const username = ref("");
    const isAdmin = ref(false);
    const isAuthenticated = ref(false);

    const updateUserInfo = () => {
      const token = localStorage.getItem("accessToken");
      isAuthenticated.value = !!token;
      isAdmin.value = localStorage.getItem("isAdmin") === "true";

      if (isAuthenticated.value) {
        const firstName = localStorage.getItem("userFirstName");
        const lastName = localStorage.getItem("userLastName");
        if (firstName && lastName) {
          username.value = `${firstName} ${lastName}`;
        } else {
          username.value = localStorage.getItem("username") || "Usuario";
        }
      } else {
        username.value = "Iniciar sesión";
      }
    };

    onMounted(() => {
      updateUserInfo();
      window.addEventListener("storage", updateUserInfo);
    });

    // Limpiar el event listener cuando el componente se desmonta
    onUnmounted(() => {
      window.removeEventListener("storage", updateUserInfo);
    });

    return {
      cartCount,
      router,
      username,
      isAdmin,
      isAuthenticated,
    };
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    // Defer first measurement to after paint so it doesn't block LCP
    requestAnimationFrame(() => this.updateNavbarHeightVariables());
    // Debounce via rAF: if multiple resize events fire in the same frame,
    // only one layout read/write happens — eliminates forced reflows.
    this._rafNavbarUpdate = () => {
      if (this._pendingNavbarRaf) return;
      this._pendingNavbarRaf = requestAnimationFrame(() => {
        this.updateNavbarHeightVariables();
        this._pendingNavbarRaf = null;
      });
    };
    window.addEventListener("resize", this._rafNavbarUpdate);

    // Initialize Bootstrap components if needed
    this.initBootstrapComponents();

    // Add scroll event listener for transparent navbar
    window.addEventListener("scroll", this.handleScroll);
  },
  unmounted() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("resize", this._rafNavbarUpdate);
    window.removeEventListener("scroll", this.handleScroll);
  },
  computed: {
    parentCategories() {
      return this.categories.filter(cat => !cat.parent);
    }
  },
  methods: {
    suggestionImgSrc(image) {
      const u = cleanMediaUrl(image);
      return u || require('@/assets/images/jamrock-logo.webp');
    },
    initBootstrapComponents() {
      // Initialize Bootstrap accordion if not using data attributes
      if (window.bootstrap && window.bootstrap.Collapse) {
        const accordionItems = document.querySelectorAll(".accordion-collapse");
        accordionItems.forEach((item) => {
          new window.bootstrap.Collapse(item, { toggle: false });
        });
      }
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      document.body.style.overflow = this.isMobileMenuOpen ? "hidden" : "";

      // Optional: Add animation effect
      const menuItems = document.querySelectorAll(
        ".mobile-link, .submenu-link"
      );
      if (this.isMobileMenuOpen) {
        menuItems.forEach((item, index) => {
          item.style.transitionDelay = `${index * 0.05}s`;
          item.style.opacity = 1;
          item.style.transform = "translateX(0)";
        });
      } else {
        menuItems.forEach((item) => {
          item.style.transitionDelay = "0s";
          item.style.opacity = 0;
          item.style.transform = "translateX(20px)";
        });
      }
    },

    toggleDesktopMenu() {
      this.isDesktopMenuOpen = !this.isDesktopMenuOpen;
      document.body.style.overflow = this.isDesktopMenuOpen ? "hidden" : "";

      // Animación similar a la del menú mobile
      const menuItems = document.querySelectorAll(
        ".desktop-link, .submenu-link"
      );
      if (this.isDesktopMenuOpen) {
        menuItems.forEach((item, index) => {
          item.style.transitionDelay = `${index * 0.05}s`;
          item.style.opacity = 1;
          item.style.transform = "translateX(0)";
        });
      } else {
        menuItems.forEach((item) => {
          item.style.transitionDelay = "0s";
          item.style.opacity = 0;
          item.style.transform = "translateX(20px)";
        });
      }
    },

    closeDesktopMenu() {
      if (this.isDesktopMenuOpen) {
        this.isDesktopMenuOpen = false;
        document.body.style.overflow = "";

        // Cerrar acordeones abiertos
        const accordions = document.querySelectorAll('.accordion-collapse.show');
        accordions.forEach(accordion => {
          if (window.bootstrap && window.bootstrap.Collapse) {
            const bsCollapse = new window.bootstrap.Collapse(accordion);
            bsCollapse.hide();
          }
        });

        // Reset menu items animation
        const menuItems = document.querySelectorAll('.desktop-link, .submenu-link');
        menuItems.forEach((item) => {
          item.style.transitionDelay = "0s";
          item.style.opacity = 0;
          item.style.transform = "translateX(20px)";
        });
      }
    },

    closeMobileMenu() {
      if (this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false;
        document.body.style.overflow = "";

        // Cerrar todos los acordeones abiertos
        const accordions = document.querySelectorAll('.accordion-collapse.show');
        accordions.forEach(accordion => {
          if (window.bootstrap && window.bootstrap.Collapse) {
            const bsCollapse = new window.bootstrap.Collapse(accordion);
            bsCollapse.hide();
          }
        });

        // Reset menu items animation
        const menuItems = document.querySelectorAll('.mobile-link, .submenu-link');
        menuItems.forEach((item) => {
          item.style.transitionDelay = "0s";
          item.style.opacity = 0;
          item.style.transform = "translateX(20px)";
        });
      }
    },

    toggleMobileSearch() {
      this.isMobileSearchOpen = !this.isMobileSearchOpen;
      document.body.style.overflow = this.isMobileSearchOpen ? 'hidden' : '';

      // Si se abre la búsqueda móvil, enfocamos el input
      if (this.isMobileSearchOpen) {
        setTimeout(() => {
          const mobileSearchInput = document.querySelector('.mobile-search-input');
          if (mobileSearchInput) {
            mobileSearchInput.focus();
          }
        }, 100);
      }
    },

    closeMobileSearch() {
      if (this.isMobileSearchOpen) {
        this.isMobileSearchOpen = false;
        document.body.style.overflow = '';
      }
    },

    mobileSearch() {
      if (this.searchQuery.trim()) {
        this.router.push({
          path: "/productos",
          query: { search: this.searchQuery.trim() },
        });
        this.closeMobileSearch();
      }
    },
    updateNavbarHeightVariables() {
      // Get navbar wrapper element
      const navbarWrapper = document.querySelector(".navbar-wrapper");
      if (navbarWrapper) {
        // Calculate navbar height
        const navbarHeight = navbarWrapper.offsetHeight;

        // Set CSS variables
        document.documentElement.style.setProperty(
          "--navbar-total-height",
          `${navbarHeight}px`
        );

        // Also set a mobile version if screen is smaller
        if (window.innerWidth <= 991.98) {
          document.documentElement.style.setProperty(
            "--navbar-total-height-mobile",
            `${navbarHeight}px`
          );

          // Set optimized hero height for mobile based on screen height
          const screenHeight = window.innerHeight;
          let optimizedHeroHeight;

          if (window.innerWidth <= 480) {
            optimizedHeroHeight = Math.max(screenHeight * 0.6, 350); // 60% of screen or at least 350px
          } else if (window.innerWidth <= 768) {
            optimizedHeroHeight = Math.max(screenHeight * 0.7, 400); // 70% of screen or at least 400px
          } else {
            optimizedHeroHeight = Math.max(screenHeight * 0.7, 450); // 70% of screen or at least 450px
          }

          document.documentElement.style.setProperty(
            "--hero-height",
            `${optimizedHeroHeight}px`
          );

          // Set a background color that matches the products section
          document.documentElement.style.setProperty(
            "--hero-bg-color",
            "#f8f8f8"
          );
        } else {
          // Reset hero height for desktop
          document.documentElement.style.removeProperty("--hero-height");
          document.documentElement.style.removeProperty("--hero-bg-color");
        }
      }
    },
    handleResize() {
      if (window.innerWidth >= 992 && this.isMobileMenuOpen) {
        this.closeMobileMenu();
      }
    },
    handleSearch() {
      if (this.searchQuery.trim()) {
        this.router.push({
          path: "/productos",
          query: { search: this.searchQuery.trim() },
        });
        this.closeMobileMenu();
      }
    },
    handleUserAccount() {
      if (this.isAuthenticated) {
        if (this.isAdmin) {
          this.router.push("/configuracion/productos");
        } else {
          this.router.push("/configuracion/my-orders");
        }
      } else {
        this.router.push("/iniciar-sesion");
      }
      this.closeMobileMenu();
    }, handleScroll() {
      const navbar = document.querySelector(".navbar");
      const promoBanner = document.querySelector(".promo-banner");

      if (window.scrollY > 150) {
        // Aplicar estilos de scroll
        if (navbar) navbar.classList.add("navbar-scrolled");
        if (promoBanner) promoBanner.classList.add("scrolled");
      } else {
        // Remover estilos de scroll
        if (navbar) navbar.classList.remove("navbar-scrolled");
        if (promoBanner) promoBanner.classList.remove("scrolled");
      }
    },
    getCategoryIcon(categoryName) {
      const icons = {
        'ART. PARA FUMAR': 'smoking',
        'PIPAS': 'tag',
        'PICADORES': 'mortar-pestle',
        'PAPELILLOS/FILTROS': 'scroll',
        'ENCENDEDORES': 'fire',
        'EXTRACCIONES': 'vial',
        'BANDEJAS/LATAS': 'box',
        'CENICEROS': 'tag',
        'COSECHA': 'scissors',
        'ARMADORES': 'tools',
        'ARTICULOS INDOOR': 'warehouse',
        'MEDICIÓN': 'ruler-combined',
        'FERTILIZANTES': 'flask',
        'FERTILIZANTES PARA CRECIMIENTO': 'seedling',
        'FERTILIZANTES PARA FLORACION': 'tag',
        'ENRAIZANTES': 'tag',
        'BIOESTIMULANTES': 'leaf',
        'CONTROL PLAGAS': 'bug-slash',
        'ILUMINACION': 'lightbulb',
        'MACETAS': 'cube',
        'SUSTRATOS': 'layer-group',
        'TABACOS': 'smoking',
        'OTROS ARTÍCULOS': 'boxes'
      };
      return icons[categoryName] || 'tag';
    },

    async loadCategories() {
      try {
        this.isLoadingCategories = true;
        console.log('Cargando categorías...');

        const response = await getCategories();
        this.categories = response.results || response;

        // Process categories and add slugs if missing
        this.categories = this.categories.map(category => ({
          ...category,
          slug: category.slug || this.generateSlug(category.name)
        }));

        console.log('Categorías procesadas:', this.categories);

        // Si la API devolvió vacío, usar categorías de respaldo
        if (!this.categories.length) {
          this.useFallbackCategories();
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        // Sin mensaje amarillo: el menú cae a categorías de respaldo que
        // llevan a una búsqueda en el catálogo (no al filtro por id).
        this.useFallbackCategories();
      } finally {
        this.isLoadingCategories = false;
      }
    },

    // Categorías de respaldo cuando la API falla o tarda demasiado.
    // Enlazan a /productos?search=<nombre> (búsqueda en catálogo), no al
    // filtro por categoría, porque sin la API no se garantiza el id/name exacto.
    useFallbackCategories() {
      this.isFallbackCategories = true;
      this.categories = [
        { name: 'ART. PARA FUMAR', parent: null },
        { name: 'ARTICULOS INDOOR', parent: null },
        { name: 'CONTROL PLAGAS', parent: null },
        { name: 'FERTILIZANTES', parent: null },
        { name: 'ILUMINACION', parent: null },
        { name: 'JAMROCK GROW SHOP', parent: null },
        { name: 'MACETAS', parent: null },
        { name: 'OTROS ARTÍCULOS', parent: null },
        { name: 'SUSTRATOS', parent: null },
        { name: 'TABACOS', parent: null },
        { name: 'VAPORIZADORES', parent: null }
      ];
    },

    // Link de una categoría: con respaldo -> búsqueda en catálogo;
    // con datos reales -> filtro por nombre de categoría.
    categoryHref(category) {
      const name = typeof category === 'string' ? category : category.name;
      if (this.isFallbackCategories) {
        return '/productos?search=' + encodeURIComponent(name);
      }
      return '/productos?categories=' + encodeURIComponent(name);
    },

    // Generate slug client side if needed
    generateSlug(name) {
      return name
        .toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w-]+/g, '')        // Remove all non-word chars
        .replace(/--+/g, '-')           // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
    },

    // Esta función es crítica para comprobar si una categoría tiene subcategorías
    hasSubcategories(categoryId) {
      // Las categorías de respaldo no tienen id ni subcategorías conocidas
      if (categoryId == null) return false;
      const hasChildren = this.categories.some(cat => cat.parent === categoryId);
      return hasChildren;
    },

    // Asegurar que estas funciones funcionan correctamente
    getDirectSubcategories(parentId) {
      const subcats = this.categories.filter(cat => cat.parent === parentId);
      return subcats;
    },

    // Método mejorado para obtener subcategorías anidadas
    getNestedSubcategories(parentId) {
      // Obtener las subcategorías directas
      const directSubcats = this.getDirectSubcategories(parentId);

      // Para cada subcategoría directa, agregar sus propias subcategorías
      return directSubcats.map(subcat => ({
        ...subcat,
        children: this.categories.filter(cat => cat.parent === subcat.id)
      }));
    },

    async handleSearchInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Si el query es muy corto, limpiar sugerencias
      if (this.searchQuery.length < 2) {
        this.suggestions = [];
        this.showSuggestions = false;
        this.isSearching = false;
        return;
      }

      // Mostrar loader inmediatamente
      this.isSearching = true;
      this.showSuggestions = true;

      // Set new timeout to avoid too many requests (reducido a 250ms)
      this.searchTimeout = setTimeout(async () => {
        try {
          const data = await getSearchSuggestions(this.searchQuery);
          this.suggestions = data.results || [];
        } catch (error) {
          console.error('Error en búsqueda:', error);
          this.suggestions = [];
        } finally {
          this.isSearching = false;
        }
      }, 250);
    },

    handleBlur() {
      // Small delay to allow click on suggestion
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200);
    },

    handleSuggestionClick(product) {
      this.router.push(`/producto/${product.id}`);
      this.searchQuery = '';
      this.showSuggestions = false;
    },
  },
};
</script>

<style scoped>
/* Estilos del banner promocional */
.promo-banner {
  background-color: var(--primary-color); /* Semi-transparente */
  padding: 0;
  font-size: 14px;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.promo-banner.scrolled {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.promo-banner.scrolled .text-white {
  color: #fff !important;
}

.promo-banner.scrolled a.text-white {
  color: #fff !important;
}

.promo-banner.scrolled a.text-white:hover {
  color: var(--primary-color) !important;
  opacity: 1;
}

.social-links {
  display: flex;
  gap: 15px;
}

.social-link {
  color: var(--text-dark);
  text-decoration: none;
  transition: opacity 0.3s ease;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-link:hover {
  opacity: 0.8;
  background-color: rgba(255, 255, 255, 0.2);
}

.timer-container {
  display: flex;
  align-items: center;
}

.timer-box {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 2px 6px;
  margin: 0 4px;
  font-weight: bold;
}

/* Estilos del menú superior */
.top-menu {
  background-color: #f8f9fa;
  padding: 8px 20px;
  font-size: 13px;
}

.top-menu-links {
  display: flex;
  align-items: center;
}

.top-menu-link {
  color: #6c757d;
  margin-right: 15px;
  text-decoration: none;
}

.top-menu-link:hover {
  color: #6a42b9;
}

.delivery-time {
  color: #6c757d;
  margin-left: 10px;
}

.highlight {
  color: #ff6b6b;
  font-weight: bold;
}

.top-menu-right {
  display: flex;
  align-items: center;
}

.language-selector,
.currency-selector {
  margin-right: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.language-selector i,
.currency-selector i {
  font-size: 10px;
  margin-left: 5px;
}

.tracking-link {
  color: #6c757d;
  text-decoration: none;
}

/* Estilos del navbar principal */
.navbar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1050;
  background: transparent;
}

.navbar {
  padding: 15px 20px;
  background-color: transparent !important;
  transition: background-color 0.3s ease, color 0.3s ease;
  box-shadow: none;
}

/* Estilo para navbar después de scroll */
.navbar-scrolled {
  background-color: rgba(0, 0, 0, 0.8) !important;
  color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.navbar-brand,
.user-account,
.account-label,
.nav-actions i,
.mobile-actions i {
  color: #fff !important;
}

.account-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.account-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: #fff;
}

.brand-container {
  display: flex;
  align-items: center;
}

.logo-img {
  width: 70px;
  height: auto;
  margin-right: 8px;
}

.brand-name {
  font-size: 20px;
  font-weight: bold;
  color: var(--navbar-text);
}

.location-selector {
  display: flex;
  align-items: center;
  margin-left: 15px;
  font-size: 13px;
  color: var(--navbar-icon);
  cursor: pointer;
  border-left: 1px solid var(--navbar-border);
  padding-left: 15px;
}

.location-selector i {
  margin-right: 5px;
  color: var(--primary-color);
}

.location-text {
  font-weight: bold;
  margin: 0 5px;
}

/* Barra de búsqueda */
.search-container {
  flex-grow: 1;
  max-width: 500px;
  margin: 0 30px;
}

.search-input {
  border-radius: 20px 0 0 20px;
  border: 1px solid var(--navbar-border);
  padding-left: 15px;
  height: 42px;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.search-btn {
  background-color: var(--black-color) !important;
  /* Forzar el color */
  color: var(--white-color) !important;
  border-radius: 0 20px 20px 0;
  border: none;
  width: 50px;
  transition: background-color 0.3s ease;
}

.search-btn:hover {
  background-color: var(--primary-dark) !important;
}

/* Estilo para botón de búsqueda en navbar oscuro */
.navbar-scrolled .search-btn {
  background-color: var(--primary-color) !important;
}

/* Íconos de acciones */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: 2rem;
}

/* Transiciones para los íconos de acciones */
.nav-actions i,
.user-account,
.cart-icon,
.account-label,
.account-text {
  transition: color 0.3s ease;
}

.user-account {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.user-account:hover {
  opacity: 0.8;
}

.account-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.account-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-dark);
}

.account-role {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 12px;
}

.wishlist-icon,
.cart-icon {
  position: relative;
  margin-left: 20px;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  text-decoration: none;
}

.wishlist-icon i,
.cart-icon i {
  font-size: 22px;
  color: var(--navbar-icon);
}

.wishlist-icon:hover,
.cart-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.wishlist-count,
.cart-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--primary-color) !important;
  /* Cambiado a color pastel */
  color: var(--white-color);
  font-size: 10px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Menú de categorías */
.category-menu {
  background-color: var(--navbar-bg);
  padding: 0 20px;
  position: relative;
  overflow: visible;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.mobile-link {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: var(--navbar-text);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}

.social-links-mobile {
  display: flex;
  gap: 10px;
  margin-right: 20px;
}

.social-link-mobile {
  color: var(--navbar-text);
  text-decoration: none;
  font-size: 1.2rem;
  transition: color 0.3s ease;
}

.social-link-mobile:hover {
  color: var(--primary-color);
}


.navbar-scrolled+.category-menu {
  background-color: #000;
}

.navbar-scrolled+.category-menu .category-item a {
  color: #fff;
}

.navbar-scrolled+.category-menu .category-item a:hover {
  color: var(--primary-color);
}

.category-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.category-item {
  margin-right: 25px;
  position: relative;
}

.category-item a {
  display: block;
  padding: 15px 0;
  color: var(--navbar-text);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.85rem;
  text-transform: capitalize;
}

.category-item a:hover {
  color: var(--primary-color);
}

/* Mejoras a los estilos del dropdown */
.mega-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: max-content;
  min-width: 400px;
  max-width: 800px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 20px;
  transform: translateY(10px);
  max-height: 80vh;
  overflow-y: auto;
}

.mega-dropdown.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-toggle .inline-fa {
  font-size: 0.7rem;
  transition: transform 0.3s ease;
}

.dropdown-toggle::after {
  display: none;
}

.has-dropdown:hover .dropdown-toggle .inline-fa {
  transform: rotate(-180deg);
}

.subcategories-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
}

.subcategory-column {
  padding: 6px;
}

.subcategory-title {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: var(--text-dark);
  text-decoration: none;
  padding: 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background-color: rgba(0, 0, 0, 0.04);
  margin-bottom: 0;
  width: 100%;
  white-space: normal;
  line-height: 1.2;
}

.subcategory-title:hover {
  background-color: rgba(0, 0, 0, 0.08);
  color: var(--primary-color);
  transform: translateX(5px);
}

.all-products {
  color: var(--primary-color) !important;
  font-weight: 600 !important;
}

/* Mobile Improvements */
@media (max-width: 991.98px) {
  .mega-dropdown {
    position: static;
    transform: none;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
  }

  .mega-content {
    grid-template-columns: 1fr;
  }

  .category-section {
    padding: 12px;
    background: none;
  }

  .subcategories-grid {
    gap: 12px;
  }

  .category-main-title {
    font-size: 1rem;
    margin-bottom: 12px;
  }

  .subcategory-title {
    font-size: 0.9rem;
  }

  .subcategory-item {
    font-size: 0.85rem;
  }
}

/* Mobile menu and other existing styles */
.mobile-menu-toggle,
.mobile-icon-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.mobile-icon-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.navbar-scrolled .mobile-icon-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.navbar-scrolled .mobile-icon-btn i {
  color: #fff;
}

.mobile-menu-toggle .inline-fa {
  transform: rotate(0deg);
}

/* Mobile Search */
.mobile-search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
  padding: 2rem 1rem;
}

.mobile-search-overlay.show {
  opacity: 1;
  visibility: visible;
}

.mobile-search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-bottom: 1rem;
}

.mobile-search-form {
  width: 100%;
  max-width: 600px;
}

.mobile-search-close {
  color: #fff;
  font-size: 1.5rem;
  background: transparent;
  border: none;
  padding: 0.5rem;
}

/* Promo banner items */
.promo-item {
  display: flex;
  align-items: center;
}

/* Enhanced mobile menu appearance */
@media (max-width: 991.98px) {
  .navbar {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .logo-img {
    width: 45px;
  }

  .navbar-brand {
    margin-right: 0;
  }

  .mobile-actions {
    gap: 5px;
  }
}

/* Fixed Offcanvas Menu */
.offcanvas-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 320px;
  height: 100vh;
  background-color: #fff;
  z-index: 1050;
  overflow-y: auto;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.offcanvas-menu.show {
  transform: translateX(0);
}

.offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1045;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.offcanvas-backdrop.show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

/* Enhanced mobile user account section */
.mobile-user-account {
  margin-bottom: 1.5rem !important;
}

.mobile-user-account a {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: var(--text-dark);
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: background-color 0.2s ease;
}

.mobile-user-account a:hover,
.mobile-user-account a:active {
  background-color: rgba(0, 0, 0, 0.06);
}

.mobile-user-account i.fas {
  font-size: 1.25rem;
}

.offcanvas-header {
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.offcanvas-logo {
  width: 35px;
  height: 35px;
  margin-right: 10px;
}

.offcanvas-body {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.offcanvas-footer {
  padding: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

/* Info Links Styles */
.info-links {
  gap: 1.25rem;
}

.info-link {
  color: var(--text-dark);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
}

.info-link:hover {
  color: var(--primary-color);
}

.info-link i {
  font-size: 0.9rem;
}

/* Search Suggestions Styles */
.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.suggestion-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.suggestion-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 12px;
}

.suggestion-info {
  flex: 1;
}

.suggestion-name {
  font-size: 0.9rem;
  color: var(--text-dark);
  margin-bottom: 2px;
}

.suggestion-price {
  font-size: 0.8rem;
  color: var(--primary-color);
  font-weight: 500;
}

/* Search Loading and No Results Styles */
.search-loading,
.search-no-results {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.search-loading {
  background: linear-gradient(90deg, #f8f9fa 0%, #fff 50%, #f8f9fa 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.search-no-results {
  color: #6c757d;
}

/* Add these styles for mobile search suggestions */
@media (max-width: 991.98px) {
  .search-suggestions {
    position: fixed;
    top: 150px;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 16px 16px 0 0;
    max-height: 50vh;
  }
}

/* Skeleton Loaders for Category Menu */
.skeleton-item {
  display: flex;
  align-items: center;
}

.skeleton-link {
  display: flex;
  align-items: center;
  width: 120px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.1);
  margin-right: 8px;
}

.skeleton-text {
  height: 14px;
  width: 80px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.mobile-skeleton-menu {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.mobile-skeleton-item {
  padding: 8px 0;
}

.skeleton-button {
  height: 40px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.6;
  }
}

/* Resaltar más el menú activo */
.has-dropdown {
  position: relative;
}

.has-dropdown::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.has-dropdown:hover::after,
.has-dropdown:focus-within::after {
  width: 100%;
}

/* Desktop Offcanvas Menu Styles */
.desktop-menu-toggle {
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.desktop-menu-toggle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.desktop-offcanvas-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 350px;
  height: 100vh;
  background-color: #fff;
  z-index: 1050;
  overflow-y: auto;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.desktop-offcanvas-menu.show {
  transform: translateX(0);
}

.desktop-offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1045;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.desktop-offcanvas-backdrop.show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.desktop-menu-item {
  margin-bottom: 0.5rem;
}

.desktop-link {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  color: var(--navbar-text);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  transition: background-color 0.2s ease;
  border-radius: 6px;
}

.desktop-link:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: var(--primary-color);
}

.desktop-skeleton-menu {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.desktop-skeleton-item {
  padding: 8px 0;
}

/* Estilo para el botón de acordeón en desktop */
.desktop-offcanvas-menu .accordion-button {
  padding: 12px 15px;
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--navbar-text);
  background-color: transparent;
  box-shadow: none;
  border-radius: 6px !important;
}

.desktop-offcanvas-menu .accordion-button:not(.collapsed) {
  color: var(--primary-color);
  background-color: rgba(0, 0, 0, 0.05);
  box-shadow: none;
}

.desktop-offcanvas-menu .accordion-button:focus {
  box-shadow: none;
  border-color: transparent;
}

.desktop-offcanvas-menu .accordion-button::after {
  margin-left: auto;
  content: "\f107";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  background-image: none;
  font-size: 0.9rem;
}

.desktop-offcanvas-menu .accordion-button:not(.collapsed)::after {
  transform: rotate(-180deg);
}

.desktop-offcanvas-menu .submenu-items {
  padding: 0.5rem 1rem 1rem 2rem;
}

.desktop-offcanvas-menu .submenu-category {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
  letter-spacing: 0.5px;
}

.desktop-offcanvas-menu .submenu-link {
  display: block;
  padding: 8px 12px;
  color: var(--navbar-text);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 4px;
  margin-bottom: 2px;
}

.desktop-offcanvas-menu .submenu-link:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: var(--primary-color);
  padding-left: 15px;
}

/* Estilos específicos para mobile */
@media (max-width: 991.98px) {
  .mobile-subcategory-link {
    display: block !important;
    width: 100% !important;
    color: var(--text-dark) !important;
    text-decoration: none !important;
    border-bottom: none !important;
    margin-bottom: 8px !important;
    padding: 8px 16px !important;
    font-size: 0.9rem !important;
    background-color: transparent !important;
  }
  
  .mobile-subcategory-link:hover {
    background-color: rgba(0, 0, 0, 0.05) !important;
    color: var(--primary-color) !important;
  }
  
  .subcategory-parent-link {
    font-weight: 500 !important;
  }
  
  .mobile-subcategory-children {
    display: flex !important;
    flex-direction: column !important;
    padding-left: 15px !important;
    margin-top: 0 !important;
  }
  
  .mobile-grandchild-link {
    color: var(--text-muted) !important;
    font-size: 0.85rem !important;
    border-bottom: none !important;
    text-decoration: none !important;
    padding: 6px 12px !important;
    margin-bottom: 4px !important;
  }
  
  .mobile-submenu-items {
    display: flex !important;
    flex-direction: column !important;
    padding: 0 !important;
  }
  
  .subcategory-with-children {
    display: flex !important;
    flex-direction: column !important;
    border-bottom: none !important;
  }
}
</style>
